import type { Product } from "../../rest/account";
import type { MarketPhase } from "../../rest/data";
import { useGetIsSubscribedToMarketData } from "../useGetIsSubscribedToMarketData";
import useGetMarketStatus from "../useGetMarketStatus";

export type LatestFeed = "sip" | "delayed_sip" | "boats" | "overnight";

export type HistoricalFeed = "sip" | "boats";

export function getFeedLabel(feed: LatestFeed | undefined): string {
  switch (feed) {
    case "sip":
      return "NBBO";
    case "delayed_sip":
      return "15Min Delayed SIP";
    case "overnight":
      return "Overnight Indicative Feed";
    case "boats":
      return "BOATS Feed";
    default:
      return "";
  }
}

interface ReviewFeedArgs {
  product: Product | undefined;
  phase: MarketPhase | undefined;
  isOvernightTradable: boolean;
  isSubscribed: boolean;
}

export function getReviewFeed({
  product,
  phase,
  isOvernightTradable,
  isSubscribed,
}: ReviewFeedArgs): LatestFeed {
  if (phase === "overnight" && isOvernightTradable) {
    return isSubscribed ? "boats" : "overnight";
  }

  if (isSubscribed) {
    return "sip";
  }

  return product === "paper" ? "delayed_sip" : "sip";
}

/**
 * Whether a review-stage quote must be fetched from the dedicated SIP snapshot
 * endpoint instead of the normal quote endpoint.
 *
 * `getReviewFeed` only returns "sip" for an unsubscribed user on a live product
 * during regular/extended hours, so this single predicate captures exactly the
 * "unsubscribed live review" case that the normal quote endpoint cannot serve.
 */
export function usesSipSnapshotEndpoint({
  isSubscribed,
  feed,
}: {
  isSubscribed: boolean;
  feed: LatestFeed;
}): boolean {
  return !isSubscribed && feed === "sip";
}

interface MarketDataFeed {
  /** Feed for latest/snapshot endpoints */
  latestFeed: LatestFeed | undefined;
  /** Feed for historical endpoints */
  historicalFeed: HistoricalFeed | undefined;
  /** Human-readable label for the active latestFeed */
  feedLabel: string;
  /** Whether the market is in overnight session AND the asset trades overnight */
  isOvernight: boolean;
  /** Whether the user has a market data subscription */
  isSubscribed: boolean;
  /** Whether subscription status is still loading */
  isLoading: boolean;
}

/**
 * Centralized hook for determining the correct market data feed parameter.
 *
 * Feed matrix:
 * | Condition                                  | Latest      | Historical |
 * |--------------------------------------------|-------------|------------|
 * | Regular/Ext + subscribed (any product)     | sip         | sip        |
 * | Regular/Ext + NOT subscribed (any product) | delayed_sip | sip        |
 * | Overnight + subscribed (any product)       | boats       | boats      |
 * | Overnight + NOT subscribed (any product)   | overnight   | boats      |
 *
 * @param isOvernightTradable - Whether the current asset supports overnight trading.
 *   Pass `asset?.attributes?.includes("overnight_tradable")`. Defaults to true
 *   so callers without asset context (e.g. multi-symbol lists) still get overnight
 *   feeds when the market is in overnight session.
 */
export const useMarketDataFeed = (
  isOvernightTradable: boolean = true,
): MarketDataFeed => {
  const { marketStatus } = useGetMarketStatus();
  const { isSubscribed, isLoading } = useGetIsSubscribedToMarketData();

  const phase = marketStatus?.phase;
  const isOvernight = phase === "overnight" && isOvernightTradable;

  if (isLoading) {
    return {
      latestFeed: undefined,
      historicalFeed: undefined,
      feedLabel: "",
      isOvernight,
      isSubscribed,
      isLoading,
    };
  }

  if (isOvernight) {
    const latestFeed: LatestFeed = isSubscribed ? "boats" : "overnight";
    return {
      latestFeed,
      historicalFeed: "boats",
      feedLabel: getFeedLabel(latestFeed),
      isOvernight,
      isSubscribed,
      isLoading,
    };
  }

  // Regular + extended hours (core / pre / post / closed)
  const latestFeed: LatestFeed = isSubscribed ? "sip" : "delayed_sip";

  return {
    latestFeed,
    // delayed_sip is a latest/stream-only feed; historical endpoints only
    // accept sip, so historical stays on sip regardless of subscription.
    historicalFeed: "sip",
    feedLabel: getFeedLabel(latestFeed),
    isOvernight,
    isSubscribed,
    isLoading,
  };
};
